import { fromJS } from 'immutable';
import moment from 'moment';
import Actions from 'flow/actions';
import {
    httpGetErrorDispatcher,
    networkErrorDispatcher,
    spawnChatNotification,
} from 'flow/actions/app-actions';
import { receiveChannelMessage } from 'flow/actions/channels-actions';
import { history } from 'flow/configure-store';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import AttachmentResource from 'flow/api/attachment';
import ChatsResource from 'flow/api/chats';
import ConferencesResource from 'flow/api/conferences';
import { selectOwnChatUserKey } from 'flow/selectors/own-extension-selectors';
import { getOwnExtensionKey } from 'flow/selectors/selectors';
import {
    selectMeetingIsActive,
    selectActiveMeetingChatSessionId,
} from 'flow/selectors/meeting-selectors';
import { sendVideoChatMessage } from 'flow/api-clients/video-chat-client';
import { ISO_8601_MS_RESOLUTION } from 'flow/utils';
import { addChatLogEntry } from 'flow/utils/browser-utils';
import { appIsVisible } from 'flow/utils/notification-utils';

const MAX_THREAD_MESSAGES_PER_PAGE = 50;

export const messageCompose = (chatsessionkey, text, threadKey = null) => {
    return {
        type: Actions.COMPOSER_UPDATE,
        key: chatsessionkey,
        threadKey,
        text: text,
    };
};

export const updatePublicMessageLoading = (status) => {
    return {
        type: Actions.PUBLIC_MESSAGE_LOADING,
        status,
    };
};

export const attachmentCompose = (
    chatsessionkey,
    attachment,
    threadMessageKey = null,
) => {
    return {
        type: Actions.ATTACHMENT_UPDATE,
        key: chatsessionkey,
        threadMessageKey,
        attachment: attachment,
    };
};

export const prepareAttachment =
    (
        sessionkey,
        clientkey,
        text,
        stylings,
        attachment,
        replyToMessage = null,
        threadMessageKey = null,
    ) =>
    (dispatch) => {
        AttachmentResource.uploadAttachment(
            {
                onResult(json) {
                    dispatch(
                        sendChatMessage(
                            sessionkey,
                            clientkey,
                            text,
                            stylings,
                            json,
                            false,
                            replyToMessage,
                            threadMessageKey,
                        ),
                    );
                },

                onError() {
                    const messageDTO = {
                        message: text,
                        attachment: attachment,
                        stylings: stylings,
                        alsoAsSMS: false,
                    };
                    dispatch(receiveFailedMessage(sessionkey, messageDTO));
                    networkErrorDispatcher(dispatch);
                },
            },
            attachment.name,
            attachment.file,
        );
    };

export const markChatMessageRead = (chatsessionkey, chatmessagekey) => {
    const messageDTO = {
        key: chatmessagekey,
    };

    return (dispatch) => {
        ChatsResource.updateMessage(
            {
                onResult(/*void*/) {
                    dispatch(receiveMessageRead(chatsessionkey, messageDTO));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            chatsessionkey,
            messageDTO,
        );
    };
};

export const markChatSessionUnread = (chatSessionKey) => (dispatch) => {
    ChatsResource.markSessionAsUnread(
        {
            onResult(json) {
                dispatch(receiveChatSessionUnread(json));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        chatSessionKey,
    );
};

export const sendChatMessage = (
    sessionkey,
    clientkey,
    text,
    stylings,
    attachment,
    alsoAsSMS,
    replyToId = null,
    threadMessageKey = null,
) => {
    const messageDTO = {
        message: text,
        attachment: attachment,
        stylings: stylings,
        alsoAsSMS: alsoAsSMS,
    };

    if (replyToId) {
        messageDTO.quotedMessage = {
            quotedMessageKey: replyToId,
        };
    }

    if (threadMessageKey) {
        messageDTO.threadMessageKey = threadMessageKey;
    }

    const parameters = !threadMessageKey
        ? [sessionkey, clientkey, messageDTO]
        : [sessionkey, threadMessageKey, clientkey, messageDTO];

    return (dispatch, getState) => {
        const state = getState();
        // Dual-write top-level text messages to the in-meeting side-channel so
        // external guests see them in their ephemeral chat panel — but ONLY when
        // the message is going to the meeting's OWN chat session. sendChatMessage
        // is the generic send for every conversation, so without the
        // session-equality guard a message typed in an unrelated DM/group while a
        // meeting is active would leak to the relay and the external guests.
        // Attachments and thread replies aren't broadcast — externals can't
        // contextualize threads and the relay doesn't support attachments.
        // Fire-and-forget: if the WS isn't open the dispatch no-ops silently.
        const activeMeetingChatSessionId =
            selectActiveMeetingChatSessionId(state);
        const isMeetingChatSession =
            !!activeMeetingChatSessionId &&
            sessionkey === `chatsession-${activeMeetingChatSessionId}`;
        if (
            !threadMessageKey &&
            text &&
            selectMeetingIsActive(state) &&
            isMeetingChatSession
        ) {
            dispatch(sendVideoChatMessage(text, false));
        }

        ChatsResource?.[
            !threadMessageKey ? 'createMessage' : 'createThreadMessage'
        ](
            {
                onResult: function onResult(json) {
                    if (threadMessageKey) {
                        dispatch(
                            receiveThreadMessage(
                                sessionkey,
                                threadMessageKey,
                                json,
                            ),
                        );
                    } else {
                        dispatch(receiveMessage(sessionkey, json));
                    }

                    if (alsoAsSMS) {
                        addChatLogEntry('User successfully sent sms');
                    }
                },
                onError(error) {
                    dispatch(receiveFailedMessage(sessionkey, messageDTO));
                    networkErrorDispatcher(dispatch);
                    if (alsoAsSMS) {
                        addChatLogEntry(
                            `Error, failed in sending sms with error code: ${error.status}`,
                        );
                    }
                },
                onClientError(error) {
                    dispatch(receiveFailedMessage(sessionkey, messageDTO));
                    if (alsoAsSMS) {
                        addChatLogEntry(
                            `Client error, failed in sending sms with error code: ${error.status}`,
                        );
                    }
                },
            },
            ...parameters,
        );
    };
};

export const sendChatPost = (
    sessionkey,
    clientkey,
    title,
    text,
    attachment,
) => {
    if (attachment) {
        return (dispatch) => {
            AttachmentResource.uploadAttachment(
                {
                    onResult(json) {
                        dispatch(
                            sendComposedChatPost(
                                sessionkey,
                                clientkey,
                                title,
                                text,
                                json,
                            ),
                        );
                    },

                    onError: networkErrorDispatcher(dispatch),
                },
                attachment.name,
                attachment.file,
            );
        };
    } else {
        return (dispatch) => {
            dispatch(
                sendComposedChatPost(
                    sessionkey,
                    clientkey,
                    title,
                    text,
                    attachment,
                ),
            );
        };
    }
};

export const likeChatPost = (sessionKey, postKey, userDTOs) => (dispatch) => {
    ChatsResource.likePost(
        {
            onResult(json) {
                dispatch(receiveChatPostLikes(sessionKey, postKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        sessionKey,
        postKey,
        userDTOs,
    );
};

export const unlikeChatPost = (sessionKey, postKey, userKey) => (dispatch) => {
    ChatsResource.deleteLike(
        {
            onResult(json) {
                dispatch(receiveChatPostLikes(sessionKey, postKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        sessionKey,
        postKey,
        userKey,
    );
};

const receiveChatPostLikes = (sessionKey, postKey, chatUsers) => ({
    type: Actions.RESULT_CHAT_POST_LIKE,
    sessionKey: sessionKey,
    postKey: postKey,
    data: chatUsers,
});

const sendComposedChatPost = (
    sessionkey,
    clientkey,
    title,
    text,
    attachment,
) => {
    const chatPostDTO = {
        DTOSubtype: 'ChatPostDTO',
        title: title,
        message: text,
        attachment: attachment,
    };

    return (dispatch) => {
        ChatsResource.createPost(
            {
                onResult(json) {
                    dispatch(receiveMessage(sessionkey, json));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            sessionkey,
            clientkey,
            chatPostDTO,
        );
    };
};

const sendComposedChatPostComment = (
    sessionkey,
    chatMessageKey,
    text,
    attachment,
) => {
    const chatPostCommentDTO = {
        message: text,
        attachment: attachment,
    };

    return (dispatch) => {
        ChatsResource.createChatComment(
            {
                onResult(json) {
                    dispatch(receiveComment(sessionkey, chatMessageKey, json));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            sessionkey,
            chatMessageKey,
            chatPostCommentDTO,
        );
    };
};

export const sendChatPostComment = (
    sessionkey,
    chatMessageKey,
    text,
    attachment,
) => {
    if (attachment) {
        return (dispatch) => {
            AttachmentResource.uploadAttachment(
                {
                    onResult(json) {
                        dispatch(
                            sendComposedChatPostComment(
                                sessionkey,
                                chatMessageKey,
                                text,
                                json,
                            ),
                        );
                    },

                    onError: networkErrorDispatcher(dispatch),
                },
                attachment.name,
                attachment.file,
            );
        };
    } else {
        return (dispatch) => {
            dispatch(
                sendComposedChatPostComment(
                    sessionkey,
                    chatMessageKey,
                    text,
                    attachment,
                ),
            );
        };
    }
};

export const joinChatSession = (chatSessionKey, chatUserDTO, onSuccess) => {
    return (dispatch) => {
        ChatsResource.invite(
            {
                onResult() {
                    onSuccess(chatSessionKey);
                },

                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionKey,
            true,
            chatUserDTO,
        );
    };
};

export const getChatPost = (chatSessionKey, postKey) => (dispatch) => {
    const splitPostKey = postKey.split('-');
    const messageOlderThanPostKey = `${splitPostKey[0]}-${splitPostKey[1] - 1}`;
    ChatsResource.getMessages(
        {
            onResult(json) {
                const chatMessages = fromJS(json);
                const chatPost = chatMessages.find(
                    (chatMessage) => chatMessage.get('key') === postKey,
                );
                if (chatPost) {
                    dispatch(receiveChatPost(chatSessionKey, chatPost));
                }
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        chatSessionKey,
        messageOlderThanPostKey,
        'contact',
    );
};

export const getChatComments =
    (chatSessionKey, chatMessageKey) => (dispatch) => {
        ChatsResource.getChatComments(
            {
                onResult(json) {
                    dispatch(
                        receiveComments(chatSessionKey, chatMessageKey, json),
                    );
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            chatSessionKey,
            chatMessageKey,
        );
    };

export const fetchChatMessages =
    (chatSessionKey, fromMessageKey, limitMessageAmount, ...include) =>
    (dispatch) => {
        ChatsResource.getMessages(
            {
                onResult(json) {
                    dispatch(
                        receiveMessages(
                            chatSessionKey,
                            json,
                            limitMessageAmount,
                            fromMessageKey,
                        ),
                    );

                    const lastMessage = json[json.length - 1];
                    if (lastMessage?.attachment && include.includes('images')) {
                        dispatch(getSessionContent(chatSessionKey, 'images'));
                    }
                },

                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionKey,
            fromMessageKey,
            include.join(),
            undefined,
            50,
        );
    };

export const fetchOlderMessages =
    (chatSessionKey, chatMessageEntityKey, ...include) =>
    (dispatch) => {
        ChatsResource.getMessageHistory(
            {
                onResult(json) {
                    dispatch(receiveOlderMessages(chatSessionKey, json));
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            chatSessionKey,
            chatMessageEntityKey,
            include.join(),
            undefined,
            25,
        );
    };

export const fetchOlderMessagesWithCallback =
    (chatSessionKey, chatMessageEntityKey, callback, ...include) =>
    (dispatch) => {
        ChatsResource.getMessageHistory(
            {
                onResult(json) {
                    callback?.();
                    dispatch(receiveOlderMessages(chatSessionKey, json));
                },
                // The callback marks the load as over, also when it failed:
                // otherwise one failed page blocks every later one and
                // leaves the list marked busy.
                onError(error) {
                    callback?.();
                    httpGetErrorDispatcher(dispatch)(error);
                },
            },
            chatSessionKey,
            chatMessageEntityKey,
            include.join(),
            undefined,
            25,
        );
    };

export const fetchChatSessions =
    (...include) =>
    (dispatch) => {
        return new Promise((resolve, reject) => {
            ChatsResource.getSessions(
                {
                    onResult: (data) => {
                        dispatch(receiveChatSessions(data));
                        resolve();
                    },
                    onError: () => {
                        httpGetErrorDispatcher(dispatch);
                        reject();
                    },
                    onClientError: reject,
                },
                include.join(),
            );
        });
    };

export const fetchPublicChatSessions = () => (dispatch) => {
    return new Promise((resolve, reject) => {
        ChatsResource.getPublicSessions({
            onResult(data) {
                dispatch(receivePublicChatSessions(data));
                resolve();
            },
            onError: () => {
                httpGetErrorDispatcher(dispatch);
                reject();
            },
            onClientError: reject,
        });
    });
};

export const fetchChatSession =
    (chatSessionKey, ...include) =>
    (dispatch) => {
        return new Promise((resolve, reject) => {
            ChatsResource.getSession(
                {
                    onResult: function onResult(json) {
                        dispatch(receiveChatSession(chatSessionKey, json));
                        resolve();
                    },
                    onError: () => {
                        httpGetErrorDispatcher(dispatch);
                        reject();
                    },
                },
                chatSessionKey,
                include.join(),
            );
        });
    };

export const receiveMessageRead = (chatSessionKey, message) => ({
    type: Actions.RESULT_CHAT_MESSAGE_READ,
    key: chatSessionKey,
    data: message,
});

export const receiveChatSessionUnread = (chatSessionDTO) => ({
    type: Actions.RESULT_CHAT_SESSION_UNREAD,
    data: chatSessionDTO,
});

export const inviteChatMember =
    (chatSessionEntityKey, chatUserDTO) => (dispatch) => {
        ChatsResource.invite(
            {
                onResult() {
                    dispatch(fetchChatSession(chatSessionEntityKey, 'contact'));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionEntityKey,
            false,
            chatUserDTO,
        );
    };

export const inviteChatMembers =
    (chatSessionEntityKey, chatUserDTOs, { onSuccess, onError } = {}) =>
    (dispatch) => {
        ChatsResource.inviteChatMemberList(
            {
                onResult(/* json */) {
                    dispatch(fetchChatSession(chatSessionEntityKey, 'contact'));
                    onSuccess?.();
                },

                onError() {
                    networkErrorDispatcher(dispatch)();
                    onError?.();
                },
            },
            chatSessionEntityKey,
            chatUserDTOs,
        );
    };

export const createAndShowChatSession =
    (chatSessionDTO, photo) => (dispatch) => {
        ChatsResource.createSession(
            {
                onResult(json) {
                    dispatch(receiveCreateSession(json.key, json));
                    if (photo) {
                        dispatch(setRoomImage(json.key, photo));
                    }
                    history.push(
                        generatePath(routes['messages.id.settings'], {
                            id: json.key,
                        }),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionDTO,
        );
    };

export const createAndShowChatSessionWithMessage =
    (
        chatSessionDTO,
        clientKey,
        text,
        attachment,
        onSuccess,
        alsoAsSMS = false,
    ) =>
    (dispatch) => {
        ChatsResource.createSession(
            {
                onResult(json) {
                    dispatch(receiveCreateSession(json.key, json));
                    if (text || attachment) {
                        if (attachment) {
                            dispatch(
                                prepareAttachment(
                                    json.key,
                                    clientKey,
                                    text,
                                    null,
                                    attachment,
                                ),
                            );
                        } else {
                            dispatch(
                                sendChatMessage(
                                    json.key,
                                    clientKey,
                                    text,
                                    null,
                                    attachment,
                                    alsoAsSMS,
                                ),
                            );
                        }
                    }
                    if (onSuccess) {
                        onSuccess(json.key);
                    } else {
                        if (!chatSessionDTO.roomType) {
                            history.push(
                                generatePath(routes['messages.id'], {
                                    id: json.key,
                                }),
                            );
                        } else {
                            history.push(
                                generatePath(routes['messages.id.settings'], {
                                    id: json.key,
                                }),
                            );
                        }
                    }
                },
                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionDTO,
        );
    };

export const createAndShowChatSessionWithSettingsClosed =
    (chatSessionDTO) => (dispatch) => {
        ChatsResource.createSession(
            {
                onResult(json) {
                    dispatch(receiveCreateSession(json.key, json));
                    history.push(
                        generatePath(routes['messages.id'], { id: json.key }),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionDTO,
        );
    };

export const createAndShowPublicChatSession =
    (chatSessionDTO, photo) => (dispatch) => {
        ChatsResource.createSession(
            {
                onResult(json) {
                    dispatch(receiveCreateSession(json.key, json));
                    if (photo) {
                        dispatch(setRoomImage(json.key, photo));
                    }
                    history.push(
                        generatePath(routes['messages.id.settings'], {
                            id: json.key,
                        }),
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionDTO,
        );
    };

/**
 * Creates a chat room session.
 *
 * @param onError Optional. When provided it REPLACES the default
 *   networkErrorDispatcher toast — it is not invoked in addition to it. Omit
 *   it to get the default network-error snackbar; pass it when the caller
 *   shows its own error feedback (otherwise the failure is silent).
 */
export const createRoomSession =
    (chatSessionDTO, photo, onSuccess, onError) => (dispatch) => {
        ChatsResource.createSession(
            {
                onResult(json) {
                    dispatch(receiveCreateSession(json.key, json));
                    if (photo) {
                        dispatch(setRoomImage(json.key, photo));
                    }
                    onSuccess(json.key);
                },
                onError(error) {
                    if (onError) {
                        onError(error);
                    } else {
                        networkErrorDispatcher(dispatch)();
                    }
                },
            },
            chatSessionDTO,
        );
    };

export const createSession = (chatUserKey) => (dispatch) => {
    const sessionDTO = {
        members: [{ key: chatUserKey }],
    };

    return new Promise((resolve, reject) => {
        ChatsResource.createSession(
            {
                onResult(json) {
                    dispatch(receiveCreateSession(chatUserKey, json));
                    resolve(json.key);
                },

                onError(error) {
                    networkErrorDispatcher(dispatch);
                    reject(error);
                },
            },
            sessionDTO,
        );
    });
};

export const createNewSession = (chatUserKey, onSuccess) => (dispatch) => {
    const sessionDTO = {
        members: [{ key: chatUserKey }],
    };

    return ChatsResource.createSession(
        {
            onResult(json) {
                dispatch(receiveCreateSession(json.key, json));
                onSuccess(json.key);
            },

            onError: networkErrorDispatcher(dispatch),
        },
        sessionDTO,
    );
};

export const receiveTopMessage =
    (sessionKey, chatMessage, silent = true) =>
    async (dispatch, getState) => {
        const channelKey = chatMessage.get('channelKey');

        if (channelKey) {
            dispatch(
                receiveChannelMessage(sessionKey, channelKey, chatMessage),
            );
        } else {
            const state = getState();
            const chatSession = state.chats.getIn(['sessions', sessionKey]);
            const isNewChat = !chatSession || !chatSession.has('key');
            const isPost = !!chatMessage.get('title');
            const isAttachment = !chatMessage.get('message'); //Top message does not include attachment info

            if (isNewChat || isPost || isAttachment) {
                await dispatch(fetchChatSession(sessionKey, 'contact'));
            }

            if (chatMessage?.get('threadMessageKey')) {
                dispatch(
                    receiveThreadMessage(
                        sessionKey,
                        chatMessage.get('threadMessageKey'),
                        chatMessage,
                        isAttachment,
                    ),
                );
                dispatch(
                    fetchChatMessages(
                        sessionKey,
                        null,
                        true,
                        'contact',
                        'images',
                        'threads',
                    ),
                );
            } else {
                dispatch(
                    receiveMessage(
                        sessionKey,
                        chatMessage,
                        isNewChat || isPost,
                        isAttachment,
                    ),
                );
            }
        }
        if (!silent && !appIsVisible(getState)) {
            const contact = chatMessage.getIn(['from', 'contact']);
            dispatch(
                spawnChatNotification(
                    sessionKey,
                    contact,
                    chatMessage,
                    channelKey,
                ),
            );
        }
    };

export const receiveTopComment = (sessionKey, comment) => (dispatch) => {
    const chatMessageKey = comment.get('chatMessageKey');
    dispatch(receiveComment(sessionKey, chatMessageKey, comment));
};

export const receiveFailedMessage =
    (sessionKey, messageDTO) => (dispatch, getState) => {
        const ownExtensionKey = getOwnExtensionKey(getState());
        const failedMessageId = getFailedMessageId(getState(), sessionKey);
        dispatch(
            receiveFailedMessageAction(
                sessionKey,
                messageDTO,
                ownExtensionKey,
                failedMessageId,
            ),
        );
    };

const getFailedMessageId = (state, sessionKey) => {
    const failedMessages = state.chats.getIn(
        ['failedMessages', sessionKey],
        fromJS([]),
    );
    const lastEntry = failedMessages.get(-1);
    if (lastEntry) {
        const lastMessageKey = lastEntry.get('key');
        const lastId = lastMessageKey.split('-').pop();
        return parseInt(lastId) + 1;
    } else {
        return 1;
    }
};

const receiveMessage = (
    sessionkey,
    json,
    isFetchDispatch = false,
    isAttachment = false,
) => {
    return {
        type: Actions.RESULT_CHAT_MESSAGE,
        key: sessionkey,
        data: json,
        isFetchDispatch,
        isAttachment,
    };
};

const receiveFailedMessageAction = (
    sessionKey,
    messageDTO,
    ownExtensionKey,
    messageKey,
) => ({
    type: Actions.RESULT_FAILED_CHAT_MESSAGE,
    key: sessionKey,
    messageDTO: messageDTO,
    messageKey: messageKey,
    ownExtensionKey: ownExtensionKey,
    dateTime: moment().format(ISO_8601_MS_RESOLUTION),
});

export const removeFailedMessageAction = (sessionKey, messageKey) => ({
    type: Actions.RESULT_REMOVE_FAILED_MESSAGE,
    sessionKey: sessionKey,
    messageKey: messageKey,
});

const receiveChatPost = (sessionKey, post) => {
    return {
        type: Actions.RESULT_CHAT_POST,
        sessionKey,
        post,
    };
};

export const receiveComment = (sessionKey, chatMessageKey, json) => {
    return {
        type: Actions.RESULT_CHAT_COMMENT,
        key: sessionKey,
        chatMessageKey: chatMessageKey,
        data: json,
    };
};

const receiveComments = (sessionKey, chatMessageKey, json) => {
    return {
        type: Actions.RESULT_CHAT_COMMENTS,
        key: sessionKey,
        chatMessageKey: chatMessageKey,
        data: json,
    };
};

const receiveMessages = (key, json, limitMessageAmount, since) => {
    return {
        type: Actions.RESULT_CHAT_MESSAGES,
        key: key,
        data: json,
        limitMessageAmount,
        // What the page was anchored on, so the reducer can tell a page that
        // continues the stored messages by construction from one that has to
        // prove it.
        since,
    };
};

const receiveOlderMessages = (key, json) => ({
    type: Actions.RESULT_OLDER_CHAT_MESSAGES,
    key: key,
    data: json,
});

const receiveChatSession = (key, json) => {
    return {
        type: Actions.RESULT_CHAT_SESSION,
        key: key,
        data: json,
    };
};

const receiveChatSessionImageChanged = (key, json) => {
    return {
        type: Actions.RESULT_CHAT_SESSION_IMAGE,
        key: key,
        data: json,
    };
};

const receiveChatSessions = (json) => {
    return {
        type: Actions.RESULT_CHAT_SESSIONS,
        data: json,
    };
};

const receivePublicChatSessions = (json) => {
    return {
        type: Actions.RESULT_PUBLIC_CHAT_SESSIONS,
        data: json,
    };
};

const receiveCreateSession = (chatUserKey, json) => ({
    type: Actions.RESULT_CHAT_CREATE_SESSION,
    key: chatUserKey,
    data: json,
});

const receiveMuteSession = (key, json) => ({
    type: Actions.RESULT_CHAT_MUTE_SESSION,
    key: key,
    data: json,
});

export const muteSession = (key, endTime) => (dispatch) => {
    const chatMutedDTO = {
        mutedUntil: endTime,
    };
    ChatsResource.muteChat(
        {
            onResult() {
                dispatch(receiveMuteSession(key, chatMutedDTO));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        key,
        chatMutedDTO,
    );
};

export const unMuteSession = (key) => (dispatch) => {
    ChatsResource.unmuteChat(
        {
            onResult() {
                dispatch(receiveMuteSession(key, null));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        key,
    );
};

const receiveArchiveSession = (key, json) => ({
    type: Actions.RESULT_CHAT_ARCHIVE_SESSION,
    key: key,
    data: json,
});

export const archiveSession = (key) => (dispatch) => {
    ChatsResource.archiveChat(
        {
            onResult(json) {
                dispatch(receiveArchiveSession(key, json));
                dispatch(receiveChatSession(key, json));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        key,
    );
};

export const unArchiveSession = (key) => (dispatch) => {
    ChatsResource.unarchiveChat(
        {
            onResult(json) {
                if (!(json.chatArchived && json.chatArchived.archived)) {
                    json.chatArchived = null;
                }
                // Dispatch actions with the updated json
                dispatch(receiveArchiveSession(key, json));
                dispatch(receiveChatSession(key, json));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        key,
    );
};

const receiveLeaveRoom = (sessionKey) => ({
    type: Actions.RESULT_CHAT_LEAVE_ROOM,
    key: sessionKey,
});

export const leaveRoom = (sessionKey, chatUserKey) => (dispatch) => {
    ChatsResource.uninvite(
        {
            onResult() {
                dispatch(receiveLeaveRoom(sessionKey));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        sessionKey,
        chatUserKey,
    );
};

export const receiveTopRemoveMemberFromChat =
    (chatSessionKey, chatUserKey) => (dispatch, getState) => {
        const ownChatUserKey = selectOwnChatUserKey(getState());
        if (ownChatUserKey === chatUserKey) {
            dispatch(receiveLeaveRoom(chatSessionKey));
            dispatch(navigateIfChatOpen(chatSessionKey));
        } else {
            dispatch(receiveRemoveChatMember(chatSessionKey, chatUserKey));
        }
    };

export const receiveTopAddMemberToChat =
    (chatSessionKey, chatUserKey, extensionKey) => (dispatch, getState) => {
        const ownChatUserKey = selectOwnChatUserKey(getState());
        if (ownChatUserKey === chatUserKey) {
            dispatch(fetchChatSession(chatSessionKey, 'contact'));
        } else {
            dispatch(
                receiveAddChatMember(chatSessionKey, chatUserKey, extensionKey),
            );
        }
    };

const navigateIfChatOpen = (chatSessionKey) => () => {
    const url = window.location.href;
    const chatOpen = url.indexOf(chatSessionKey) > -1;
    if (chatOpen) {
        history.replace(routes['messages']);
    }
};

const receiveRemoveChatMember = (sessionKey, chatUserKey) => ({
    type: Actions.RESULT_CHAT_REMOVE_CHAT_MEMBER,
    key: sessionKey,
    chatUserKey: chatUserKey,
});

const receiveAddChatMember = (chatSessionKey, chatUserKey, extensionKey) => ({
    type: Actions.RESULT_CHAT_ADD_CHAT_MEMBER,
    key: chatSessionKey,
    chatUserKey: chatUserKey,
    extensionKey: extensionKey,
});

export const removeChatMember = (sessionKey, chatUserKey) => (dispatch) => {
    ChatsResource.uninvite(
        {
            onResult() {
                dispatch(receiveRemoveChatMember(sessionKey, chatUserKey));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        sessionKey,
        chatUserKey,
    );
};

export const updateChatSession =
    (sessionKey, chatSessionDTO, callback) => (dispatch) => {
        ChatsResource.updateChatRoom(
            {
                onResult() {
                    dispatch(receiveChatSession(sessionKey, chatSessionDTO));
                    callback?.();
                },

                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionDTO,
        );
    };

export const addAdmin = (chatSessionKey, chatUserKey) => (dispatch) => {
    const chatUserDTO = {
        key: chatUserKey,
    };

    return ChatsResource.addAdmin(
        {
            onResult(json) {
                dispatch(receiveAddAdmin(chatSessionKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        chatSessionKey,
        chatUserDTO,
    );
};

export const receiveTopAddAdmin =
    (chatSessionKey, chatUserKey) => (dispatch) => {
        dispatch(receiveAddAdmin(chatSessionKey, chatUserKey));
    };

const receiveAddAdmin = (chatSessionKey, json) => ({
    type: Actions.RESULT_ADD_CHAT_ADMIN,
    key: chatSessionKey,
    data: json,
});

export const removeAdmin = (chatSessionKey, chatUserKey) => (dispatch) => {
    return ChatsResource.removeAdmin(
        {
            onResult(json) {
                dispatch(receiveRemoveAdmin(chatSessionKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        chatSessionKey,
        chatUserKey,
    );
};

export const receiveTopRemoveAdmin =
    (chatSessionKey, chatUserKey) => (dispatch) => {
        dispatch(receiveRemoveAdmin(chatSessionKey, chatUserKey));
    };

const receiveRemoveAdmin = (chatUserKey, json) => ({
    type: Actions.RESULT_REMOVE_CHAT_ADMIN,
    key: chatUserKey,
    data: json,
});

export const setRoomImage = (sessionkey, file) => (dispatch) => {
    ChatsResource.setRoomImage(
        {
            onResult(json) {
                dispatch(receiveChatSessionImageChanged(sessionkey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        sessionkey,
        file,
    );
};

export const deleteRoomImage = (sessionkey) => (dispatch) => {
    ChatsResource.deleteRoomImage(
        {
            onResult() {
                dispatch(receiveChatSessionImageDeleted(sessionkey));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        sessionkey,
    );
};

const receiveChatSessionImageDeleted = (key) => ({
    type: Actions.RESULT_CHAT_SESSION_IMAGE_DELETED,
    key,
});

export const showImagePreview = (imageUrl) => ({
    type: Actions.CHAT_PREVIEW_IMAGE,
    data: imageUrl,
});

export const getChatConference = (sessionKey, meetingType) => (dispatch) => {
    return new Promise((resolve, reject) => {
        ConferencesResource.getChatConference(
            {
                onResult(json) {
                    resolve(json);
                },
                onError: (error) => {
                    networkErrorDispatcher(dispatch);
                    reject(error);
                },
            },
            sessionKey,
            'contact,live',
            meetingType,
        );
    });
};

export const generateChatConferencePin = (sessionKey) => (dispatch) => {
    return new Promise((resolve, reject) => {
        ConferencesResource.generateChatConferencePin(
            {
                onResult(json) {
                    resolve(json);
                },
                onError: (error) => {
                    networkErrorDispatcher(dispatch);
                    reject(error);
                },
            },
            sessionKey,
            'contact,live',
        );
    });
};

export const getChatSessionDetails = (sessionKey) => (dispatch) => {
    ChatsResource.getChatSessionDetails(
        {
            onResult(json) {
                dispatch(receiveChatDetails(json, sessionKey));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        sessionKey,
    );
};

const receiveChatDetails = (details, sessionKey) => ({
    type: Actions.RESULT_CHAT_DETAILS,
    data: details,
    key: sessionKey,
});

export const getSessionContent = (sessionKey, type) => (dispatch) => {
    ChatsResource.getMessages(
        {
            onResult(json) {
                dispatch(receiveSessionContent(json, sessionKey, type));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        sessionKey,
        null,
        'contact',
        type,
    );
};

export const getSessionContentHistory =
    (sessionKey, lastIndex, type) => (dispatch) => {
        ChatsResource.getMessageHistory(
            {
                onResult(json) {
                    dispatch(
                        receiveSessionContentHistory(json, sessionKey, type),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            sessionKey,
            lastIndex,
            'contact',
            type,
        );
    };

const receiveSessionContent = (content, sessionKey, type) => ({
    type: Actions.RESULT_CHAT_CONTENT,
    data: content,
    key: sessionKey,
    contentType: type,
});

const receiveSessionContentHistory = (content, sessionKey, type) => ({
    type: Actions.RESULT_CHAT_CONTENT_HISTORY,
    data: content,
    key: sessionKey,
    contentType: type,
});

export const createReaction = (messageReactionSession) => (dispatch) => {
    const {
        chatSessionKey,
        chatMessageKey: messageKey,
        shortcode,
        threadParentKey = null,
    } = messageReactionSession;

    const reactionDTO = {
        type: shortcode,
    };

    ChatsResource.createReaction(
        {
            onResult(json) {
                dispatch({
                    type: Actions.RESULT_CHAT_REACTION,
                    chatSessionKey,
                    messageKey,
                    threadParentKey,
                    data: json,
                });
            },

            onError: networkErrorDispatcher(dispatch),
        },
        chatSessionKey,
        messageKey,
        reactionDTO,
    );
};

export const getReactions = (chatSessionKey, messageKey) => (dispatch) => {
    ChatsResource.getReactionsByMessageId(
        {
            onResult(json) {
                dispatch({
                    type: Actions.GET_CHAT_REACTIONS,
                    chatSessionKey,
                    messageKey,
                    data: json,
                });
            },

            onError: networkErrorDispatcher(dispatch),
        },
        chatSessionKey,
        messageKey,
    );
};

export const updateReactions =
    (chatSessionKey, messageKey, json, threadParentKey = null) =>
    (dispatch) => {
        dispatch({
            type: Actions.RESULT_CHAT_REACTION,
            chatSessionKey: `chatsession-${chatSessionKey}`,
            messageKey: `chatmessage-${messageKey}`,
            threadParentKey: threadParentKey
                ? `chatmessage-${threadParentKey}`
                : null,
            data: json,
        });
    };

export const deleteReaction = (messageReactionSession) => (dispatch) => {
    const {
        chatSessionKey,
        chatMessageKey: messageKey,
        shortcode,
        threadParentKey = null,
    } = messageReactionSession;

    ChatsResource.deleteReaction(
        {
            onResult(json) {
                dispatch({
                    type: Actions.DELETE_CHAT_REACTION,
                    chatSessionKey,
                    messageKey,
                    shortcode,
                    threadParentKey,
                    data: json,
                });
            },

            onError: networkErrorDispatcher(dispatch),
        },
        chatSessionKey,
        messageKey,
        shortcode,
    );
};

export const addQuotedMessage = (payload) => ({
    type: Actions.ADD_QUOTED_MESSAGE,
    payload,
});

export const removeQuotedMessage = (payload) => ({
    type: Actions.REMOVE_QUOTED_MESSAGE,
    payload,
});
export const openSelectedThreadMessage = (chatSessionKey, chatMessageKey) => ({
    type: Actions.OPEN_SELECTED_THREAD_MESSAGE,
    chatSessionKey,
    chatMessageKey,
});

export const clearSelectedThreadMessage = () => ({
    type: Actions.CLEAR_SELECTED_THREAD_MESSAGE,
});

export const receiveThreadMessages = (
    content,
    chatSessionKey,
    chatMessageKey,
) => ({
    type: Actions.RECEIVE_THREAD_MESSAGES,
    data: content,
    chatSessionKey,
    chatMessageKey,
});

export const receiveThreadMessage = (
    chatSessionKey,
    chatMessageKey,
    content,
) => ({
    type: Actions.RECEIVE_THREAD_MESSAGE,
    data: content,
    chatSessionKey,
    chatMessageKey,
});

export const getThreadChatMessages =
    (chatSessionEntityKey, chatMessageEntityKey, beforeMessageKey) =>
    async (dispatch) => {
        ChatsResource.getThreadMessages(
            {
                onResult(json) {
                    dispatch(
                        receiveThreadMessages(
                            json,
                            chatSessionEntityKey,
                            chatMessageEntityKey,
                        ),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionEntityKey,
            chatMessageEntityKey,
            beforeMessageKey,
            MAX_THREAD_MESSAGES_PER_PAGE,
        );
    };

export const receiveThreadMessageRead = (
    chatSessionKey,
    parentThreadMessageKey,
) => ({
    type: Actions.RESULT_CHAT_THREAD_MESSAGE_READ,
    chatSessionKey,
    parentThreadMessageKey,
});

export const attachmentImageLoad = () => ({
    type: Actions.ATTACHMENT_IMAGE_LOADED,
});

export const readThreadChatMessage =
    (chatSessionEntityKey, threadParentKey, chatMessageEntityKey) =>
    (dispatch) => {
        const chatReadThreadDTO = {
            messageKey: chatMessageEntityKey,
        };

        ChatsResource.readThread(
            {
                onResult(/*void*/) {
                    dispatch(
                        receiveThreadMessageRead(
                            chatSessionEntityKey,
                            threadParentKey,
                        ),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionEntityKey,
            threadParentKey,
            chatReadThreadDTO,
        );
    };
